// ============================================================
// IndicateursCarte — les deux indicateurs du §2bis, et leur verdict.
// ------------------------------------------------------------
// Sortie de `ApprentissagePage.jsx` le 28 septembre 2026, avec la règle
// du seuil : sous `seuil` pièces chiffrées dans le mois, le serveur ne
// rend PAS de taux (`sous_seuil`), et l'écran affiche l'effectif —
// « 0 sur 6 » et non « 0 % ». C'est la règle de `ecartParFamille`.
//
// Le verdict ne lit que les mois AU-DESSUS du seuil. En septembre
// 2026 il concluait « aucune des deux ne monte » en comparant 25 pièces
// d'août à 6 de septembre : la conclusion la plus forte de l'écran, sur
// l'échantillon le plus mince.
// ============================================================

import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Badge from '../../ui/Badge.jsx';
import { formatPct } from '../../lib/format.js';
import Sparkline from './Sparkline.jsx';

/** Un taux s'il existe, sinon l'effectif : « 0 sur 6 ». */
function part(r, taux, n) {
  return r.sous_seuil || taux == null ? `${n} sur ${r.pieces}` : `${formatPct(taux)} (${n})`;
}

/**
 * LE VERDICT, calculé sur les DEUX courbes — jamais auto-déclaré.
 *
 * « Apprend » n'est vrai que si les deux montent. Une seule qui monte
 * se produit pour de mauvaises raisons — c'est écrit dans le bloc, et
 * un badge ne doit pas dire l'inverse du texte qu'il surplombe.
 */
export function verdictIndicateurs(mesurables) {
  if (mesurables.length < 2) return { tone: 'neutral', label: 'pas encore mesurable' };
  const a = mesurables[0];
  const z = mesurables[mesurables.length - 1];
  const monte = (x, y) => x != null && y != null && y > x;
  const g = monte(a.taux_gammes_acceptees, z.taux_gammes_acceptees);
  const c = monte(a.taux_sans_consigne, z.taux_sans_consigne);
  if (g && c) return { tone: 'ok', label: 'les deux montent' };
  if (g || c) return { tone: 'warn', label: 'une seule des deux monte' };
  return { tone: 'warn', label: 'aucune des deux ne monte' };
}

export default function IndicateursCarte({ indicateurs }) {
  const ind = indicateurs;
  const seuil = ind[0]?.seuil ?? 10;
  const mesurables = ind.filter((m) => !m.sous_seuil);
  const sousSeuil = ind.filter((m) => m.sous_seuil);
  const verdict = verdictIndicateurs(mesurables);

  return (
    <Card>
      <CardHead title="Les deux indicateurs qui disent si le programme a appris">
        <Badge tone={verdict.tone}>{verdict.label}</Badge>
      </CardHead>
      <CardBody className="stack">
        <p className="field__hint">
          Les deux doivent <strong>monter</strong>, et ils se lisent{' '}
          <strong>ensemble</strong>. Pris isolément ils montent pour de mauvaises raisons : le
          second monte tout seul si plus personne n’écrit de consigne parce que l’écran
          décourage, le premier monte si le deviseur cesse de corriger parce qu’il ne fait plus
          confiance. C’est leur montée <strong>simultanée</strong> qui veut dire quelque chose.
        </p>

        <div className="grid-2">
          <div>
            <p className="caps">Gammes acceptées sans modification</p>
            <Sparkline
              points={ind.map((p) => ({ x: p.mois, y: p.taux_gammes_acceptees }))}
              sens="monte"
            />
            <p className="field__hint">
              La gamme est le pivot : quand elle est bonne, les temps le sont, et le prix aussi.
              <strong> C’est l’indicateur qui comptera dans six mois.</strong> Définition
              stricte : aucune ligne touchée à la main <em>et</em> aucune consigne — une gamme
              qui n’est devenue juste que parce qu’on a dicté la méthode n’est pas une gamme que
              l’agent a trouvée.
            </p>
          </div>
          <div>
            <p className="caps">Pièces chiffrées sans consigne</p>
            <Sparkline
              points={ind.map((p) => ({ x: p.mois, y: p.taux_sans_consigne }))}
              sens="monte"
            />
            <p className="field__hint">
              La consigne est un filet de départ, pas la cible. Chaque consigne écrite
              aujourd’hui est une leçon stockée : leur nombre doit <strong>baisser</strong>.
              S’il ne baisse pas au bout de trois mois, la mémoire ne sert à rien — et il faut
              le dire.
            </p>
          </div>
        </div>

        <Table
          caption="Mois par mois"
          columns={[
            { key: 'mois', label: 'Mois' },
            // Le dénominateur d'abord : un taux sur trois pièces n'est
            // pas une tendance, et c'est la première chose à voir.
            { key: 'pieces', label: 'Pièces chiffrées' },
            {
              key: 'taux_gammes_acceptees',
              label: 'Gammes acceptées',
              render: (r) => part(r, r.taux_gammes_acceptees, r.gammes_acceptees),
            },
            {
              key: 'taux_sans_consigne',
              label: 'Sans consigne',
              render: (r) => part(r, r.taux_sans_consigne, r.sans_consigne),
            },
            {
              // Ce que la définition stricte ÉCARTE, gardé visible :
              // des gammes non touchées, mais qui ont demandé une
              // consigne. Les cacher ferait croire que l'agent s'est
              // trompé alors qu'il a été aidé.
              key: 'acceptees_apres_consigne',
              label: 'dont acceptées après consigne',
            },
          ]}
          rows={ind}
          getRowKey={(r) => r.mois}
        />

        {mesurables.length < 2 ? (
          <p className="field__hint">
            <strong>Pas encore lisible.</strong> Il faut deux mois d’au moins {seuil} pièces
            chiffrées pour qu’une tendance existe
            {sousSeuil.length
              ? ` — ${sousSeuil.map((m) => `${m.mois} : ${m.pieces} pièce(s)`).join(', ')}`
              : ''}
            . Sous ce seuil une pièce pèse plus de {Math.round(100 / seuil)} points : on affiche
            l’effectif, pas un pourcentage.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
